import { useState } from 'react';
import useToggle from 'react-use/lib/useToggle';
import styled, { css } from 'styled-components';
import { IconChevron } from '../icon/IconChevron';
import { Checkbox } from '../input/Checkbox';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { Stack } from '../layout/Stack';
import { Typography } from '../typography/Typography';
import { CategoryUI } from './types';

const EXPAND_ANIMATION_TIME = 200;

interface ChildrensNodeeProps {
    open: boolean;
    childrenNodeHeight: number | undefined;
}

const ChildrensNode = styled(Box)
    .withConfig({ displayName: 'ChildrensNode' })
    .attrs((props) => ({
        ...props,
        overflow: 'hidden',
    }))<ChildrensNodeeProps>`
    ${({ open, childrenNodeHeight }: ChildrensNodeeProps) =>
        open
            ? css`
                  max-height: ${childrenNodeHeight}px;
              `
            : css`
                  max-height: 0;
              `}
`;

const makeTransition = ({ ms, styles }: { styles: string[]; ms: number }) =>
    styles.map((style) => `${style} ${ms}ms linear`).join(', ');

interface CategoryPickerProps {
    category: CategoryUI;
    level: number;
    updateCategoryState: (categoryId: number, newState: boolean) => void;
}

const CategoryPicker = (props: CategoryPickerProps) => {
    const { category, level, updateCategoryState } = props;
    const [open, setOpen] = useToggle(false);
    const [childrenNodeHeight, setChildrenNodeHeight] = useState<number | undefined>(undefined);

    return (
        <Flex>
            <Flex pl={!category.children && level === 0 ? 4 : 0} direction="row" spacing={1} py={1}>
                {category.children ? (
                    <Flex
                        transform={open ? 'rotate(180deg)' : 'rotate(0deg)'}
                        transition={makeTransition({
                            ms: EXPAND_ANIMATION_TIME,
                            styles: ['transform'],
                        })}
                    >
                        <IconChevron
                            size={24}
                            color="var(--color-primary)"
                            onClick={() => setOpen()}
                        />
                    </Flex>
                ) : null}
                <Checkbox
                    checked={category.checked}
                    indeterminate={category.indeterminate}
                    onChange={() => updateCategoryState(category.id, !category.checked)}
                />
                <Typography onClick={() => updateCategoryState(category.id, !category.checked)}>
                    {category.name}
                </Typography>
            </Flex>
            {category.children ? (
                <ChildrensNode
                    open={open}
                    childrenNodeHeight={childrenNodeHeight}
                    transition={makeTransition({
                        ms: EXPAND_ANIMATION_TIME,
                        styles: ['max-height'],
                    })}
                >
                    <Box
                        pl={6 * (level + 1)}
                        ref={(node) => {
                            node?.offsetHeight && setChildrenNodeHeight(node.offsetHeight);
                        }}
                    >
                        <CategoriesPicker
                            level={level + 1}
                            selectedCategoriesTree={category.children}
                            updateCategoryState={updateCategoryState}
                        />
                    </Box>
                </ChildrensNode>
            ) : null}
        </Flex>
    );
};

interface CategoriesPickerProps {
    selectedCategoriesTree: CategoryUI[];
    level?: number;
    updateCategoryState: (categoryId: number, newState: boolean) => void;
}

export const CategoriesPicker = (props: CategoriesPickerProps) => {
    const { selectedCategoriesTree, level = 0, updateCategoryState } = props;

    return (
        <Stack
            direction="column"
            count={selectedCategoriesTree.length}
            renderItem={(index) => (
                <CategoryPicker
                    level={level}
                    category={selectedCategoriesTree[index]}
                    updateCategoryState={updateCategoryState}
                />
            )}
        />
    );
};
